import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Plane, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(form.email, form.password);
      toast.success('Connexion réussie !');
      
      if (!user.email_verified && !user.phone_verified) {
        navigate('/settings#security');
      } else if (!user.terms_accepted) {
        navigate('/verify');
      } else {
        const redirect = sessionStorage.getItem('kacos_redirect_after_login');
        sessionStorage.removeItem('kacos_redirect_after_login');
        navigate(redirect || '/hub');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00em0wLTEwYzAtMi4yIDEuOC00IDQtNHM0IDEuOCA0IDQtMS44IDQtNCA0LTQtMS44LTQtNHptMC0xMGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0LTEuOCA0LTQgNC00LTEuOC00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-foreground">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-14 w-auto"
            />
          </Link>
          
          <h1 className="font-heading text-4xl font-bold mb-4">
            Bienvenue sur Kacos
          </h1>
          <p className="text-lg text-foreground max-w-md">
            La marketplace sécurisée pour l'achat et la vente de kilos de bagages 
            entre voyageurs vérifiés.
          </p>
          
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-foreground">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-bold">1</span>
              </div>
              <span>Utilisateurs vérifiés</span>
            </div>
            <div className="flex items-center gap-3 text-foreground">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-bold">2</span>
              </div>
              <span>Paiements sécurisés</span>
            </div>
            <div className="flex items-center gap-3 text-foreground">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-bold">3</span>
              </div>
              <span>Billets vérifiés</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="login-back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Retour</span>
          </button>
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_60b0b1ea-0b06-47a8-bc3e-0e0d910144d6/artifacts/xd1x3snr_kacos-logo-final-transparent.svg" 
              alt="Kacos Logo" 
              className="h-10 w-auto"
            />
          </Link>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="font-heading text-2xl">Connexion</CardTitle>
              <CardDescription>
                Entrez vos identifiants pour accéder à votre compte
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="vous@exemple.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 h-12"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="pl-10 pr-10 h-12"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 btn-primary"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>

                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                    Mot de passe oublié ?
                  </Link>
                </div>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Pas encore de compte ?{' '}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  S'inscrire
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
